import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './hooks/useAuth';
import { Layout, ProtectedRoute, AdminRoute, InstructorRoute } from './components';
import { Login, Dashboard, Labs, LabDetail, RunDetail, GradeReport, Unauthorized, NotFound, AdminDashboard, UserManagement, UserDocs, HelpLanding, HelpDetail, ChangePassword, Profile } from './pages';
import { LabManagement } from './pages/admin/LabManagement';
import { LabGradingDetailsPage } from './pages/admin/LabGradingDetails';
import { SystemMonitoring } from './pages/admin/SystemMonitoring';
import { SystemSettings } from './pages/admin/SystemSettings';
import { AdminLayout } from './components/admin/AdminLayout';
import { AdminFeedback } from './pages/admin/Feedback';
import { MyLabs } from './pages/MyLabs';
import { SSHKeys } from './pages/SSHKeys';
import { SpacesCredentials } from './pages/SpacesCredentials';
import { InstructorPanel } from './pages/instructor/InstructorPanel';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
        <div className="App">
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/userdocs" element={<UserDocs />} />
            <Route path="/change-password" element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            } />
            
            {/* Protected routes with layout */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Labs page */}
            <Route path="/labs" element={
              <ProtectedRoute>
                <Layout>
                  <Labs />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Help landing */}
            <Route path="/help" element={
              <ProtectedRoute>
                <Layout>
                  <HelpLanding />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Help detail */}
            <Route path="/help/:slug" element={
              <ProtectedRoute>
                <Layout>
                  <HelpDetail />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Individual lab detail page */}
            <Route path="/labs/:slug" element={
              <ProtectedRoute>
                <Layout>
                  <LabDetail />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Run detail page */}
            <Route path="/runs/:runId" element={
              <ProtectedRoute>
                <Layout>
                  <RunDetail />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Grade report page */}
            <Route path="/runs/:runId/grade" element={
              <ProtectedRoute>
                <Layout>
                  <GradeReport />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/runs" element={
              <ProtectedRoute>
                <Layout>
                  <MyLabs />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/ssh-keys" element={
              <ProtectedRoute>
                <Layout>
                  <SSHKeys />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/spaces-credentials" element={
              <ProtectedRoute>
                <Layout>
                  <SpacesCredentials />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/instructor" element={
              <InstructorRoute>
                <Layout>
                  <InstructorPanel />
                </Layout>
              </InstructorRoute>
            } />

            {/* Admin routes */}
            <Route path="/admin" element={
              <AdminRoute>
                <Layout>
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </Layout>
              </AdminRoute>
            } />
            
            <Route path="/admin/users" element={
              <AdminRoute>
                <Layout>
                  <AdminLayout>
                    <UserManagement />
                  </AdminLayout>
                </Layout>
              </AdminRoute>
            } />
            
            <Route path="/admin/labs" element={
              <AdminRoute>
                <Layout>
                  <AdminLayout>
                    <LabManagement />
                  </AdminLayout>
                </Layout>
              </AdminRoute>
            } />
            
            <Route path="/admin/lab-grading" element={
              <AdminRoute>
                <Layout>
                  <AdminLayout>
                    <LabGradingDetailsPage />
                  </AdminLayout>
                </Layout>
              </AdminRoute>
            } />
            
            <Route path="/admin/monitoring" element={
              <AdminRoute>
                <Layout>
                  <AdminLayout>
                    <SystemMonitoring />
                  </AdminLayout>
                </Layout>
              </AdminRoute>
            } />
            
            <Route path="/admin/settings" element={
              <AdminRoute>
                <Layout>
                  <AdminLayout>
                    <SystemSettings />
                  </AdminLayout>
                </Layout>
              </AdminRoute>
            } />
            <Route path="/admin/feedback" element={
              <AdminRoute>
                <Layout>
                  <AdminLayout>
                    <AdminFeedback />
                  </AdminLayout>
                </Layout>
              </AdminRoute>
            } />
            
            <Route path="/admin/*" element={
              <AdminRoute>
                <Layout>
                  <div className="p-8 text-white bg-gray-900 min-h-screen">
                    <h1 className="text-2xl font-bold mb-4">Admin Feature</h1>
                    <p className="text-gray-400">This admin feature is not yet implemented.</p>
                  </div>
                </Layout>
              </AdminRoute>
            } />

            {/* Redirects */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            {/* 404 catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
